import { Box, Button, CloseButton, Drawer, Flex, Grid, Portal, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import { Sidebar } from './Sidebar'

/**
 * Where the first thing on a surface sits. See `./layout`.
 *
 * Re-exported here so that it and `panelGutter` — the two insets the shell
 * imposes on what it holds — are reached from one import.
 */
export { mastheadInset } from './layout'

/**
 * The horizontal gutter anything in the panel sits inside.
 *
 * Exported because the panel is a bare portal target — it holds whatever it is
 * given, at whatever inset that thing chooses — and two panels choosing
 * differently is two panels that do not look like the same application. Shared
 * here rather than remembered in each.
 *
 * Hoisted: an object literal in JSX would be a new prop on every render.
 */
export const panelGutter = { base: 4, md: 6 }

interface PanelSlot {
  node: HTMLDivElement | null
  setPresent: (present: boolean) => void
  /**
   * Opens the panel where it is not already in a track of its own.
   *
   * Below `xl` the panel is off screen until something opens it, so a route
   * that puts something new in it has to say so — otherwise the buyer clicks a
   * row and nothing appears to happen, which is worse than the click doing
   * nothing at all. Above `xl` the panel is always visible and this changes
   * nothing.
   */
  reveal: () => void
  /**
   * Names what is in the panel, for the control that opens it.
   *
   * The shell does not know what it is holding and must not guess: a button
   * reading "Brief" above a panel offering somewhere to start one is a label
   * describing the last thing that lived there. Whatever fills the panel says
   * what to call it.
   */
  setLabel: (label: string) => void
}

const PanelSlotContext = createContext<PanelSlot | null>(null)

interface AppShellProps {
  children: ReactNode
  /** Rendered in the sidebar, above the spacer. Where navigation goes. */
  sidebarNav?: ReactNode
  /** Rendered at the foot of the sidebar. Where the account area goes. */
  sidebarFooter?: ReactNode
}

/**
 * Sidebar, conversation, panel — as grid tracks, at three widths.
 *
 *   >= xl   sidebar | conversation | panel      all three side by side
 *   lg-xl   sidebar | conversation              panel opens over the conversation
 *   < lg              conversation              sidebar and panel both open over it
 *
 * The columns are `auto | minmax(0, 1fr) | auto`. The `minmax(0, …)` is the
 * load-bearing part: a bare `1fr` takes its content as an automatic minimum, so
 * a long message would grow the column and push the panel off the right edge
 * instead of scrolling inside it.
 *
 * Below `xl` the panel is the same DOM node, repositioned — fixed to the right
 * edge rather than occupying a track. Keeping one node matters because it is a
 * portal target: moving the content between two mount points would unmount and
 * remount the brief on every resize past the breakpoint.
 */
export function AppShell({ children, sidebarNav, sidebarFooter }: AppShellProps) {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [present, setPresent] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [label, setLabel] = useState(defaultPanelLabel)

  const reveal = useCallback(() => {
    setPanelOpen(true)
  }, [])

  const slot = useMemo(() => ({ node, setPresent, reveal, setLabel }), [node, reveal])

  const togglePanel = useCallback(() => {
    setPanelOpen((open) => !open)
  }, [])

  const closePanel = useCallback(() => {
    setPanelOpen(false)
  }, [])

  return (
    <Grid
      h="100dvh"
      maxH="100dvh"
      overflow="hidden"
      templateColumns={shellColumns}
      templateRows="100%"
    >
      <Sidebar nav={sidebarNav} footer={sidebarFooter} display={sidebarDisplay} />

      <Grid templateRows="auto minmax(0, 1fr)" minW="0" minH="0" overflow="hidden">
        {/*
          Both children are placed explicitly. `hideFrom` is `display: none`,
          which takes the bar out of the grid altogether — and without an
          explicit row the conversation then auto-places into row 1, the bar's
          `auto` row, sizing itself to its content and leaving the `1fr` row
          empty beneath it. That is a composer stranded mid-page above a field
          of nothing.
        */}
        <TopBar
          sidebarNav={sidebarNav}
          sidebarFooter={sidebarFooter}
          showPanelToggle={present}
          panelOpen={panelOpen}
          panelLabel={label}
          onTogglePanel={togglePanel}
        />

        {/*
          Scrolling belongs to the route, not to this track. A chat pins its
          composer and scrolls only the messages above it.
        */}
        <Box gridRow="2" minH="0" overflow="hidden" display="grid" gridTemplateRows="100%">
          <PanelSlotContext value={slot}>{children}</PanelSlotContext>
        </Box>
      </Grid>

      {/* Dismisses the panel when it is overlaying rather than in its own track. */}
      {panelOpen && (
        <Box
          onClick={closePanel}
          position="fixed"
          inset="0"
          bg="blackAlpha.400"
          zIndex="overlay"
          hideFrom="xl"
        />
      )}

      <Box
        as="aside"
        ref={setNode}
        position={panelPosition}
        insetY={panelInsetY}
        right={panelRight}
        zIndex={panelZIndex}
        display={panelOpen ? panelDisplayOpen : panelDisplayClosed}
        w="panel"
        maxW={panelMaxW}
        my="3"
        mr="3"
        bg="bg.panel"
        borderWidth="1px"
        borderColor="border"
        borderRadius="l3"
        boxShadow={panelShadow}
        overflowY="auto"
        // Nothing portalled in means no card at all, rather than an empty one
        // floating beside the conversation.
        css={emptyIsHidden}
      />

      {/*
        A sibling rather than a child: the panel is a portal target and has to
        stay empty-detectable, so nothing can be rendered inside it. Overlaying
        the panel covers the toggle that opened it, and a backdrop edge is not
        a close button anyone can find on a phone.
      */}
      {panelOpen && (
        <CloseButton
          hideFrom="xl"
          onClick={closePanel}
          aria-label={`Close ${label}`}
          position="fixed"
          top="5"
          right="5"
          zIndex="modal"
          size="sm"
        />
      )}
    </Grid>
  )
}

/**
 * The controls that only exist when something has been taken out of view.
 *
 * Above `xl` the sidebar and the panel are both on screen and this bar has
 * nothing to offer, so it is not rendered at all rather than sitting empty.
 */
function TopBar({
  sidebarNav,
  sidebarFooter,
  showPanelToggle,
  panelOpen,
  panelLabel,
  onTogglePanel,
}: {
  sidebarNav?: ReactNode
  sidebarFooter?: ReactNode
  showPanelToggle: boolean
  panelOpen: boolean
  panelLabel: string
  onTogglePanel: () => void
}) {
  return (
    <Flex
      gridRow="1"
      hideFrom="xl"
      align="center"
      justify="space-between"
      gap="3"
      px="4"
      py="3"
      borderBottomWidth="1px"
      borderColor="border.muted"
    >
      <Drawer.Root placement="start" size="xs">
        <Drawer.Trigger asChild>
          <Button variant="ghost" size="sm" hideFrom="lg" aria-label="Menu" px="2">
            <MenuIcon />
          </Button>
        </Drawer.Trigger>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            {/*
              Sized to the sidebar, not to the drawer's own `xs`.

              The drawer here is not a panel containing the sidebar — below
              `lg` it *is* the sidebar, and it should measure the same either
              way. Left at `xs` it is 320px against the sidebar's 240, and the
              80px difference renders as a strip of `bg.panel` down the inside
              edge: a pale band between the dark sidebar and the backdrop,
              which reads as a rendering fault rather than as chrome.

              Set on the content rather than by widening the sidebar to fill
              it, because the sidebar has a designed width and the drawer is
              the thing with no opinion.
            */}
            <Drawer.Content w="sidebar" maxW="sidebar">
              <Sidebar nav={sidebarNav} footer={sidebarFooter} h="full" />
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      <Text hideFrom="lg" fontWeight="semibold" letterSpacing="heading">
        Scout
      </Text>

      <Box flex="1" />

      {/*
        The label is written in lower case by whatever fills the panel, because
        it reads mid-sentence when the panel is open. The closed form is
        capitalised here rather than carried as a second string, so the occupant
        names itself once.
      */}
      {showPanelToggle && (
        <Button variant="outline" size="sm" onClick={onTogglePanel} letterSpacing="ui">
          {panelOpen ? `Hide ${panelLabel}` : sentenceCase(panelLabel)}
        </Button>
      )}
    </Flex>
  )
}

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" fill="currentColor">
      <rect x="1" y="3" width="16" height="1.6" rx="0.8" />
      <rect x="1" y="8.2" width="16" height="1.6" rx="0.8" />
      <rect x="1" y="13.4" width="16" height="1.6" rx="0.8" />
    </svg>
  )
}

/**
 * What the panel is called when its occupant does not say.
 *
 * The brief, because that is what has filled it since it existed and the admin
 * still passes nothing.
 */
const defaultPanelLabel = 'brief'

/**
 * Raises the first letter and leaves the rest alone.
 *
 * Not `text-transform: capitalize`, which would turn "how to start" into "How
 * To Start", and not `::first-letter`, which needs a block container and
 * silently does nothing on the inline-flex box a button actually is. It was
 * written the second way first, and shipped a lower-case label.
 */
function sentenceCase(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1)
}

// Hoisted: object literals in JSX would be new props on every render.
const emptyIsHidden = { '&:empty': { display: 'none' } }
const shellColumns = {
  base: 'minmax(0, 1fr)',
  lg: 'auto minmax(0, 1fr)',
  xl: 'auto minmax(0, 1fr) auto',
}
const sidebarDisplay = { base: 'none', lg: 'flex' }
const panelPosition = { base: 'fixed', xl: 'static' } as const
const panelInsetY = { base: '0', xl: 'auto' }
const panelRight = { base: '0', xl: 'auto' }
const panelZIndex = { base: 'modal', xl: 'auto' }
const panelDisplayOpen = { base: 'block', xl: 'block' }
const panelDisplayClosed = { base: 'none', xl: 'block' }
const panelMaxW = { base: 'calc(100vw - 1.5rem)', xl: '46vw' }
const panelShadow = { base: 'lg', xl: 'none' }

/**
 * Fills the shell's panel slot. Renders beside the conversation, in the card.
 *
 * Deliberately untyped beyond its children — the brief sits here today, and a
 * comparison view would sit here on the same terms. The shell has no reason to
 * know which it is holding.
 */
/**
 * Opens the panel, for a route that has just put something new in it.
 *
 * A no-op above `xl`, where the panel has a track of its own and is never
 * closed, and outside a shell, where there is no panel to open.
 */
export function useRevealPanel(): () => void {
  const slot = use(PanelSlotContext)
  const reveal = slot?.reveal

  return useCallback(() => {
    reveal?.()
  }, [reveal])
}

export function Panel({
  children,
  label = defaultPanelLabel,
}: {
  children: ReactNode
  /** What to call this, in lower case, for the control that opens it. */
  label?: string | undefined
}) {
  const slot = use(PanelSlotContext)
  const setPresent = slot?.setPresent
  const setLabel = slot?.setLabel

  // Tells the shell there is something worth opening, so the toggle appears
  // only when pressing it would show something.
  useEffect(() => {
    if (setPresent === undefined || setLabel === undefined) return () => undefined
    setPresent(true)
    setLabel(label)
    return () => {
      setPresent(false)
      setLabel(defaultPanelLabel)
    }
  }, [setPresent, setLabel, label])

  if (slot?.node === null || slot?.node === undefined) return null
  return createPortal(children, slot.node)
}
